/*
 * Source links (careiq.n3.2, CONTRACT §18; SOURCE-LINKS-SURVEY §2.3, §3).
 * Pure: no React, no network.
 *
 *   recommendation.sources[]   vetted guideline links, primary first:
 *       {type: "guideline", id, title, org, year, version, url,
 *        status: current | update_in_progress | unverified,
 *        origin: registry | careiq, verified_on}
 *     The UI links the primary REGISTRY entry only ("Guideline: USPSTF May
 *     2021 · Colorectal Cancer: Screening ↗", new tab, https only). A
 *     model-supplied link (origin "careiq", status "unverified") is not shown
 *     while the owner has not approved one (SHOW_UNVERIFIED_LINKS).
 *   recommendation.evidence[] / evidence_more   the patient's own records:
 *       {kind: lab | imaging | document, id, title, date, when, value,
 *        open: {target: lab | imaging | document, ref}}
 *     lab ref {lrid, date, tests[], panel}: the lab popup of that result date
 *     (the lab list has no lrid, so it is found by date and test names);
 *     document ref {documentid, resource_id, category}: the document viewer
 *     (Follow up's "Open report" triple).
 *   followup.rows[].open   the row's own record, same {target, ref}.
 *
 * An n3.1 body has none of these: every helper answers null / [] and the row
 * renders exactly as before; a Follow up lab row then opens by its own date.
 * provenance.sources[].guideline.link (the todo file's link) is never read.
 */
import dayjs from "dayjs";
import { shortDate } from "./recsModel";

// Owner decision (SOURCE-LINKS-SURVEY §4.4): links come from the vetted
// registry only. The server does not send model links either
// (guidelines.SERVE_TODO_LINKS off; most of the old ones were dead, superseded
// or index pages). Showing them, marked "unverified", means flipping both.
export const SHOW_UNVERIFIED_LINKS = false;
const SERVED_STATUS = ["current", "update_in_progress"];

const str = (v) => (v == null ? "" : String(v).trim());
const normName = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const ISO_DAY = /^\d{4}-\d{2}-\d{2}/;

// Only an absolute https URL is ever rendered (the registry's are all https).
export const safeHttpsUrl = (url) => {
  const s = str(url);
  if (!/^https:\/\//i.test(s)) return null;
  try {
    const u = new URL(s);
    return u.protocol === "https:" && u.hostname ? u.href : null;
  } catch (_) {
    return null;
  }
};

/* ───────── guideline ───────── */

const linkable = (s) => {
  if (!s || typeof s !== "object" || (s.type && s.type !== "guideline") || !safeHttpsUrl(s.url)) return false;
  if (s.origin === "registry" && SERVED_STATUS.includes(s.status)) return true;
  return SHOW_UNVERIFIED_LINKS && s.origin === "careiq";
};

const orgVersion = (s) => [str(s.org), str(s.version)].filter(Boolean).join(" ");

// The guideline link of an item, or null (no registry entry: the item keeps
// its plain grey source text).
export const guidelineView = (item) => {
  const list = (item && Array.isArray(item.sources) ? item.sources : []).filter(linkable);
  if (!list.length) return null;
  const g = list[0];
  const unverified = g.origin !== "registry";
  const label = orgVersion(g) || (unverified ? "Source" : "Guideline");
  const title = str(g.title);
  const pending = g.status === "update_in_progress";
  const also = list.slice(1)
    .map((s) => [orgVersion(s), str(s.title)].filter(Boolean).join(": ") + (s.status === "update_in_progress" ? " (update in progress)" : ""))
    .filter(Boolean);
  const tip = [
    title || label,
    orgVersion(g),
    pending ? "an update is in progress at the source" : "",
    unverified ? "link from CareIQ, not verified" : "",
    also.length ? `also: ${also.join("; ")}` : "",
  ].filter(Boolean).join(" — ");
  return { href: safeHttpsUrl(g.url), label, title, tip, pending, unverified, id: g.id || null };
};

// The provenance "when" segment is the model's bare source acronym when the
// item has no state ("USPSTF", "AHA/ACC", "KDIGO 2024"); the guideline link replaces it.
export const whenIsBareSource = (item) => {
  const when = str(item && item.provenance && item.provenance.when).toLowerCase();
  if (!when) return false;
  const names = new Set();
  ((item && item.sources) || []).forEach((s) => {
    if (!s || !s.org) return;
    const org = str(s.org).toLowerCase();
    names.add(org);
    [s.version, s.year].map(str).filter(Boolean).forEach((v) => names.add(`${org} ${v.toLowerCase()}`));
  });
  ((item && item.provenance && item.provenance.sources) || []).forEach((s) => {
    const src = s && s.guideline && s.guideline.source;
    if (src) names.add(str(src).toLowerCase());
  });
  return names.has(when);
};

/* ───────── evidence ───────── */

// "LDL 162 mg/dL (H)" -> {name: "LDL", value: "162", units: "mg/dL", flag: "H"}.
export const parseTest = (t) => {
  const s = str(t);
  const m = /^(.*?\S)\s+([<>]=?\s*)?(-?\d[\d,]*(?:\.\d+)?)\s*([^()]*?)\s*(?:\(([^)]*)\))?\s*$/.exec(s);
  if (!m) return { name: s, value: "", units: "", flag: "", text: s };
  return { name: m[1], value: `${(m[2] || "").replace(/\s+/g, "")}${m[3]}`, units: str(m[4]), flag: str(m[5]), text: s };
};

// The day a served evidence item's record is opened at, and shown at (the value lead's date,
// recLanes.js): a lab its result date (open.ref.date, else the item's date), anything else its date.
export const recordDate = (ev) => {
  if (!ev || typeof ev !== "object") return "";
  const open = ev.open && typeof ev.open === "object" ? ev.open : null;
  const ref = open && open.ref && typeof open.ref === "object" ? open.ref : null;
  if (open && open.target === "lab" && ref && ref.date) return str(ref.date);
  return str(ev.date);
};

// A served {kind, title, date, when, value, open:{target, ref}} as the record
// the UI opens, or null when it cannot be opened (a lab needs its result
// date, a document an id; an unknown target is skipped).
export const openRecord = (ev, refYear) => {
  const open = ev && typeof ev === "object" && ev.open && typeof ev.open === "object" ? ev.open : null;
  const ref = open && open.ref && typeof open.ref === "object" ? open.ref : null;
  if (!ref) return null;
  const title = str(ev.title);
  const value = str(ev.value);
  if (open.target === "lab") {
    const date = recordDate(ev).slice(0, 10);
    if (!ISO_DAY.test(date)) return null;
    return {
      kind: "lab", key: `lab:${str(ref.lrid) || date}`, date, when: str(ev.when) || shortDate(date, refYear),
      title: title || str(ref.panel) || "Lab results", value, panel: str(ref.panel),
      tests: (Array.isArray(ref.tests) ? ref.tests : []).map(str).filter(Boolean),
    };
  }
  if (open.target === "imaging" || open.target === "document") {
    if (!ref.documentid && !ref.resource_id) return null;
    const date = recordDate(ev).slice(0, 10);
    return {
      kind: "doc", key: `doc:${ref.category || ""}:${ref.documentid || ref.resource_id}`, date: date || null,
      when: str(ev.when) || shortDate(date, refYear), title: title || (open.target === "imaging" ? "Imaging" : "Document"),
      imaging: open.target === "imaging",
      ref: { documentid: ref.documentid || null, resource_id: ref.resource_id || null, category: ref.category || (open.target === "imaging" ? "imagingresult" : "clinicaldocument") },
    };
  }
  return null;
};

const firstValue = (value) => str(String(value || "").split(" · ")[0]);

// The one-line link text: "LDL 162 mg/dL (H) · Aug 12 Lipid Panel",
// "Renal ultrasound · Sep 19" (", 2025" when the year differs from the DOS).
// A panel title that only repeats the value's test is left out
// ("Hemoglobin A1c 7.9 % (H) · May 30").
export const recordText = (r) => {
  if (r.kind === "lab") {
    const lead = firstValue(r.value);
    if (!lead) return [r.title, r.when].filter(Boolean).join(" · ");
    const name = normName(parseTest(lead).name);
    const title = normName(r.title);
    const repeats = Boolean(name && title && (title.includes(name) || name.includes(title)));
    const tail = [r.when, repeats ? "" : r.title].filter(Boolean).join(" ");
    return tail ? `${lead} · ${tail}` : lead;
  }
  return [r.title, r.when].filter(Boolean).join(" · ");
};

// What the screen reader hears / the tooltip says.
export const recordAria = (r) => (r.kind === "lab"
  ? `Open lab results, ${r.when}: ${r.value || r.title}`
  : `Open ${r.imaging ? "imaging report" : "document"}: ${[r.title, r.when].filter(Boolean).join(", ")}`);
export const recordTip = (r) => (r.kind === "lab"
  ? `${[r.title, r.when].filter(Boolean).join(", ")}${r.value ? `: ${r.value}` : ""}`
  : `Open ${[r.title, r.when].filter(Boolean).join(", ")}`);

// The item's openable records (server order: newest first), and how many
// more the server matched beyond them.
export const evidenceList = (item, refYear) =>
  (item && Array.isArray(item.evidence) ? item.evidence : []).map((ev) => openRecord(ev, refYear)).filter(Boolean);
export const evidenceMore = (item) => {
  const n = Number(item && item.evidence_more);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

// Anything to link under the item (evidence or a guideline)?
export const hasSourceLinks = (item) => evidenceList(item).length > 0 || Boolean(guidelineView(item));

// A Follow up row's own record: its served `open` (n3.2), or, for an n3.1
// lab row, its result date and analyte ("Potassium" / "6.1 mmol/L"; a panel
// row "Comprehensive metabolic panel, Aug 28" names its panel).
export const rowRecord = (row, refYear) => {
  if (!row) return null;
  if (row.open) {
    const r = openRecord({ title: row.text, value: row.value ? `${str(row.text)} ${str(row.value)}` : "", date: row.date, open: row.open }, refYear);
    if (r && r.kind === "lab") return { ...r, title: r.panel || r.title };
    return r;
  }
  if (row.kind !== "fu_lab" || !ISO_DAY.test(str(row.date))) return null;
  const text = str(row.text);
  const value = str(row.value);
  const date = str(row.date).slice(0, 10);
  if (value) return { kind: "lab", key: row.key, date, when: shortDate(date, refYear), title: "", value: `${text} ${value}`, panel: "", tests: [text] };
  const panel = text.replace(/,\s*[A-Z][a-z]{2} \d{1,2}(, \d{4})?$/, "");
  return { kind: "lab", key: row.key, date, when: shortDate(date, refYear), title: panel, value: "", panel, tests: [panel] };
};

/* ───────── the lab popup (LabsMiniModal) for one result date ───────── */

// The lab list grouped by result day, exactly as the Patient Timeline groups
// it (the list has no lab id, so a record is found by its date).
export const labsByDay = (labPanels) => {
  const byDay = {};
  (labPanels || []).forEach((panel) => {
    const pName = (panel && (panel.panel_name || panel.panelname)) || "";
    ((panel && panel.tests) || []).forEach((test) => {
      ((test && test.results) || []).forEach((result) => {
        if (!result) return;
        const d = dayjs(result.date);
        if (!d.isValid()) return;
        const key = d.format("YYYY-MM-DD");
        if (!byDay[key]) byDay[key] = { date: d, tests: [], panels: new Set() };
        byDay[key].tests.push({ ...test, panelname: pName, latestResult: result });
        if (pName) byDay[key].panels.add(pName);
      });
    });
  });
  return byDay;
};

// The names a record is about (for the popup's mark; never a filter).
export const focusNames = (names) => (names || []).map((n) => normName(parseTest(n).name)).filter(Boolean);

export const testMatchesFocus = (testName, focus) => {
  const n = normName(testName);
  if (!n || !focus || !focus.length) return false;
  return focus.some((f) => f && (n === f || n.split(" ").includes(f) || (f.length >= 4 && n.includes(f)) || (n.length >= 4 && f.includes(n))));
};

// The names to mark for a lab record: its tests, else the analytes of its
// value, else its panel.
const recordFocus = (r) => {
  if (r.tests && r.tests.length) return r.tests;
  const vals = String(r.value || "").split(" · ").map(str).filter(Boolean);
  if (vals.length) return vals;
  return r.panel ? [r.panel] : [];
};

// labGroup for LabsMiniModal: every result of that day (the same popup the
// timeline opens), with the record's own tests to mark. null when the loaded
// list has nothing on that day.
export const labGroupFor = (byDay, record) => {
  const day = str(record && record.date).slice(0, 10);
  const g = byDay && byDay[day];
  if (!g || !g.tests.length) return null;
  return { dateStr: g.date.format("MMM D, YYYY"), tests: g.tests, focus: focusNames(recordFocus(record)) };
};

// The popup from the record alone (the list is not loaded, failed, or does
// not reach that date): the key values it carries, no ranges.
export const labGroupFromRecord = (record) => {
  const d = dayjs(str(record && record.date).slice(0, 10));
  const parsed = String((record && record.value) || "").split(" · ").map(parseTest).filter((t) => t.name && t.value);
  return {
    dateStr: d.isValid() ? d.format("MMM D, YYYY") : "—",
    tests: parsed.map((t) => ({
      testname: t.name,
      panelname: str(record.panel || record.title) || "Results",
      refrange: "",
      flagged: Boolean(t.flag) && !/^n(ormal)?$/i.test(t.flag),
      latestResult: { value: t.value, units: t.units },
    })),
    focus: focusNames(recordFocus(record)),
    partial: true,
  };
};
